/**
 * EmergencyFeeDisclosure - Transparent Emergency Posting Fee Component
 *
 * Ethical Monetization Principles:
 * 1. TRANSPARENCY: Clearly explain WHY there's a fee (user-centric, not infra jargon)
 * 2. FAIRNESS: Fee covers actual costs, not exploitation
 * 3. CHOICE: Always offer the free standard option with clear ETA
 * 4. VALUE: Show what they get for the fee (speed + reach)
 *
 * IMPORTANT ETHICAL GUARDRAILS:
 * - Emergency fees NEVER affect ranking or visibility
 * - No priority placement - emergency only affects notification SPEED
 * - Fee cap is HARD-ENFORCED server-side (not just client)
 * - Fee data is stored immutably for auditing
 */

import React, { useState } from 'react';
import {
  ExclamationTriangleIcon,
  BoltIcon,
  CheckCircleIcon,
  ClockIcon,
  UserGroupIcon,
  XMarkIcon,
  InformationCircleIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

// Fee structure - cost-based, not profit-based
const EMERGENCY_BASE_FEE = 4.99; // Instant processing cost
const PER_CONTRACTOR_COST = 0.05; // Per-notification cost (internal)
const MAX_FEE_CAP = 14.99; // Hard cap to prevent exploitation

/**
 * Calculate emergency posting fee based on actual costs
 * Fee is capped to prevent emergency exploitation
 */
export function calculateEmergencyFee(contractorCount = 50) {
  const baseFee = EMERGENCY_BASE_FEE;
  const notificationCost = Math.min(contractorCount * PER_CONTRACTOR_COST, MAX_FEE_CAP - baseFee);
  const totalFee = Math.min(baseFee + notificationCost, MAX_FEE_CAP);

  return {
    baseFee,
    notificationCost: parseFloat(notificationCost.toFixed(2)),
    totalFee: parseFloat(totalFee.toFixed(2)),
    contractorCount,
    // Immutable snapshot for auditing (stored with request)
    breakdown: {
      instantProcessing: baseFee,
      expandedReach: parseFloat(notificationCost.toFixed(2))
    },
    // Metadata for server-side validation
    feeVersion: '1.0',
    calculatedAt: new Date().toISOString()
  };
}

/**
 * Emergency Fee Disclosure Modal
 * @param {Object} props
 * @param {string} props.theme - 'dark' or 'light' theme (default: 'light')
 */
const EmergencyFeeDisclosure = ({
  isOpen,
  onClose,
  onConfirm,
  onChooseStandard,
  estimatedContractors = 25,
  serviceTags = [],
  theme = 'light'
}) => {
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Calculate fee details on every render when open (no useEffect delay)
  const feeDetails = isOpen ? calculateEmergencyFee(estimatedContractors) : null;

  // Theme-aware styles
  const styles = theme === 'dark' ? {
    backdrop: 'bg-black/70 backdrop-blur-sm',
    modal: 'bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-xl border border-amber-500/30',
    closeBtn: 'text-gray-400 hover:text-white',
    headerIcon: 'bg-amber-500/20 border border-amber-500/30',
    headerIconColor: 'text-amber-400',
    title: 'text-white',
    subtitle: 'text-gray-400',
    infoBox: 'bg-blue-500/10 border border-blue-500/30 rounded-xl backdrop-blur-sm',
    infoIcon: 'text-blue-400',
    infoText: 'text-blue-200',
    infoTitle: 'text-blue-300',
    strongText: 'text-white',
    sectionTitle: 'text-white',
    itemText: 'text-gray-300',
    noteText: 'text-gray-500',
    feeBox: 'bg-slate-700/50',
    feeLabel: 'text-gray-300',
    feeAmount: 'text-white',
    breakdownBtn: 'text-blue-400 hover:text-blue-300',
    breakdownBorder: 'border-slate-600',
    breakdownText: 'text-gray-400',
    breakdownTotal: 'text-white',
    primaryBtn: 'bg-amber-500 hover:bg-amber-600 text-white',
    secondaryBtn: 'bg-slate-700 hover:bg-slate-600 text-gray-300',
    standardBox: 'bg-green-500/10 border border-green-500/30',
    standardText: 'text-green-300'
  } : {
    backdrop: 'bg-black/50',
    modal: 'bg-white',
    closeBtn: 'text-gray-400 hover:text-gray-600',
    headerIcon: 'bg-amber-100',
    headerIconColor: 'text-amber-600',
    title: 'text-gray-900',
    subtitle: 'text-gray-600',
    infoBox: 'bg-blue-50 border border-blue-200 rounded-lg',
    infoIcon: 'text-blue-600',
    infoText: 'text-blue-800',
    infoTitle: 'text-blue-800',
    strongText: 'font-bold',
    sectionTitle: 'text-gray-900',
    itemText: 'text-gray-700',
    noteText: 'text-gray-500',
    feeBox: 'bg-gray-50',
    feeLabel: 'text-gray-900',
    feeAmount: 'text-gray-900',
    breakdownBtn: 'text-blue-600 hover:text-blue-800',
    breakdownBorder: 'border-gray-200',
    breakdownText: 'text-gray-600',
    breakdownTotal: 'text-gray-900',
    primaryBtn: 'bg-amber-500 hover:bg-amber-600 text-white',
    secondaryBtn: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
    standardBox: 'bg-green-50 border border-green-200',
    standardText: 'text-green-800'
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 ${styles.backdrop} transition-opacity`}
        onClick={onClose}
      />

      {/* Modal — fixed center */}
      <div className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center p-4">
        <div className={`pointer-events-auto ${styles.modal} rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto`}>
          {/* Close button */}
          <button
            onClick={onClose}
            className={`absolute top-4 right-4 ${styles.closeBtn}`}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          {/* Header */}
          <div className="flex items-center mb-6">
            <div className={`flex-shrink-0 w-12 h-12 ${styles.headerIcon} rounded-full flex items-center justify-center mr-4`}>
              <BoltIcon className={`h-6 w-6 ${styles.headerIconColor}`} />
            </div>
            <div>
              <h2 className={`text-xl font-bold ${styles.title}`}>Emergency Request Fee</h2>
              <p className={`text-sm ${styles.subtitle}`}>Transparency about costs</p>
            </div>
          </div>

          {/* Why we charge - USER-CENTRIC, no infra jargon */}
          <div className={`${styles.infoBox} p-4 mb-6`}>
            <div className="flex items-start">
              <InformationCircleIcon className={`h-5 w-5 ${styles.infoIcon} mt-0.5 mr-2 flex-shrink-0`} />
              <div className={`text-sm ${styles.infoText}`}>
                <p className={`font-medium mb-1 ${styles.infoTitle}`}>Why is there a fee?</p>
                <p>
                  Emergency requests are sent <strong className={styles.strongText}>instantly</strong> to available providers
                  with expanded reach to ensure fast responses. This fee covers the cost of
                  real-time notifications — <strong className={styles.strongText}>we don't mark it up.</strong>
                </p>
              </div>
            </div>
          </div>

          {/* What you get - Speed and reach, NOT ranking favoritism */}
          <div className="mb-6">
            <h3 className={`text-sm font-medium ${styles.sectionTitle} mb-3`}>What you get:</h3>
            <div className="space-y-2">
              <div className={`flex items-center text-sm ${styles.itemText}`}>
                <BoltIcon className="h-5 w-5 text-amber-500 mr-2" />
                <span><strong>Instant notifications</strong> to ~{estimatedContractors} contractors</span>
              </div>
              <div className={`flex items-center text-sm ${styles.itemText}`}>
                <UserGroupIcon className="h-5 w-5 text-green-500 mr-2" />
                <span><strong>Expanded reach</strong> to emergency-available contractors</span>
              </div>
              <div className={`flex items-center text-sm ${styles.itemText}`}>
                <ClockIcon className="h-5 w-5 text-green-500 mr-2" />
                <span><strong>Faster responses</strong> — typically under 1 hour</span>
              </div>
              <div className={`flex items-center text-sm ${styles.itemText}`}>
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                <span>Only contractors who opted-in for emergencies</span>
              </div>
            </div>
            {/* Ethical clarification - emergency doesn't affect ranking */}
            <p className={`text-xs ${styles.noteText} mt-3 italic`}>
              Note: Emergency posting affects notification speed only — it does not change
              how contractors are ranked or selected.
            </p>
          </div>

          {/* Fee display */}
          <div className={`${styles.feeBox} rounded-lg p-4 mb-6`}>
            <div className="flex justify-between items-center mb-2">
              <span className={`text-lg font-medium ${styles.feeLabel}`}>Emergency Posting Fee</span>
              <span className={`text-2xl font-bold ${styles.feeAmount}`}>${feeDetails.totalFee}</span>
            </div>

            {/* Fee breakdown toggle */}
            <button
              onClick={() => setShowBreakdown(!showBreakdown)}
              className={`text-sm ${styles.breakdownBtn} flex items-center`}
            >
              {showBreakdown ? 'Hide' : 'Show'} fee breakdown
              <svg className={`ml-1 h-4 w-4 transform ${showBreakdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showBreakdown && (
              <div className={`mt-3 pt-3 border-t ${styles.breakdownBorder} text-sm space-y-1`}>
                <div className={`flex justify-between ${styles.breakdownText}`}>
                  <span>Instant processing</span>
                  <span>${feeDetails.breakdown.instantProcessing.toFixed(2)}</span>
                </div>
                <div className={`flex justify-between ${styles.breakdownText}`}>
                  <span>Expanded reach (~{estimatedContractors} contractors)</span>
                  <span>${feeDetails.breakdown.expandedReach.toFixed(2)}</span>
                </div>
                <div className={`flex justify-between font-medium ${styles.breakdownTotal} pt-1 border-t ${styles.breakdownBorder}`}>
                  <span>Total (capped at ${MAX_FEE_CAP})</span>
                  <span>${feeDetails.totalFee.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="space-y-3">
            <button
              onClick={() => onConfirm(feeDetails)}
              className={`w-full ${styles.primaryBtn} font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center`}
            >
              <BoltIcon className="h-5 w-5 mr-2" />
              Accept Emergency Fee (${feeDetails.totalFee}) & Continue
            </button>

            <button
              onClick={onChooseStandard}
              className={`w-full ${styles.secondaryBtn} font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center`}
            >
              <ClockIcon className="h-5 w-5 mr-2" />
              Post as Standard Request (Free)
            </button>
          </div>

          {/* Standard option explanation - clear ETA */}
          <div className={`mt-4 ${styles.standardBox} rounded-lg p-3`}>
            <p className={`text-xs ${styles.standardText} text-center`}>
              <strong>Standard requests are always free.</strong>
              <br />
              Typical response time: 2-6 hours during business hours.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Inline Emergency Fee Notice (for form display)
 * @param {Object} props
 * @param {string} props.theme - 'dark' or 'light' theme (default: 'light')
 */
export const EmergencyFeeNotice = ({
  estimatedContractors = 25,
  onLearnMore,
  theme = 'light'
}) => {
  const feeDetails = calculateEmergencyFee(estimatedContractors);

  // Theme-aware styles
  const styles = theme === 'dark' ? {
    container: 'bg-amber-500/10 border border-amber-500/30 rounded-xl backdrop-blur-sm',
    icon: 'text-amber-400',
    title: 'text-amber-300',
    text: 'text-amber-200/80',
    meta: 'text-amber-300/70',
    link: 'text-amber-400 hover:text-amber-300'
  } : {
    container: 'bg-amber-50 border border-amber-200 rounded-lg',
    icon: 'text-amber-600',
    title: 'text-amber-900',
    text: 'text-amber-800',
    meta: 'text-amber-700',
    link: 'text-amber-700 hover:text-amber-900'
  };

  return (
    <div className={`${styles.container} p-4`}>
      <div className="flex items-start">
        <BoltIcon className={`h-5 w-5 ${styles.icon} mt-0.5 mr-3 flex-shrink-0`} />
        <div className="flex-1">
          <h4 className={`text-sm font-medium ${styles.title}`}>
            Emergency Posting Fee: ${feeDetails.totalFee}
          </h4>
          <p className={`text-sm ${styles.text} mt-1`}>
            Emergency requests bypass our cache to instantly notify ~{estimatedContractors} contractors.
            This fee covers our direct costs — no markup.
          </p>
          <div className={`mt-2 flex items-center space-x-4 text-xs ${styles.meta}`}>
            <span className="flex items-center">
              <ClockIcon className="h-4 w-4 mr-1" />
              ~1 hour response
            </span>
            <span className="flex items-center">
              <UserGroupIcon className="h-4 w-4 mr-1" />
              {estimatedContractors} contractors
            </span>
          </div>
          {onLearnMore && (
            <button
              type="button"
              onClick={onLearnMore}
              className={`mt-2 text-sm ${styles.link} underline`}
            >
              Learn more about this fee
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmergencyFeeDisclosure;

